import * as React from 'react';
import { Provider } from 'mobx-react';
import { configure } from 'mobx';
import { Composer } from './Composer';
import { Store } from './stores';
import { IInstance } from '../../interfaces/IInstance';
import { ITargetingApplications } from '../../interfaces/ITargetingApplications';
import { ITargetingExistingStrucuture } from '../../interfaces/ITargetingExistingStructure';
import { ITargetingFields } from '../../interfaces/ITargetingFields';
import { ITenantSettings } from '../../interfaces/ITenantSettings';
import { IUser } from '../../interfaces/IUser';
import { IApprover } from '../../interfaces/IApprover';
import { IComposerComponent } from '../../interfaces/IComposerTool';
import { IMessage, IMessageIAConfiguration } from '../../interfaces/IMessage';
import { IFontSlots } from '../../interfaces/IFontSlots';
import { IEnvironment } from '../../interfaces/IEnvironment';
import { IRecurrenceData } from '../../interfaces/IRecurrenceData';

declare global {
  interface Window { webkit: any; app: any; android: WebAppInterface; platform: string }
  interface WebAppInterface { postMessage(message: string): any; }
}

export interface IComposerProps {
  id: number;
  open: boolean;
  token: string;
  tenant: string;
  environment: IEnvironment;
  instance: IInstance;
  component: "Messages" | "Event" | "ContentCreator" | "ContentOrganizer";
  user: IUser;
  userRole: "Administrator" | "Editor" | "Reader";
  author?: IUser;
  highlightColor: string;
  placeholderColor: string;
  imageList: string;
  videoList: string;
  isMobile: boolean;
  startDate: any;
  endDate: any;
  allDayEvent: boolean;
  isBotInstalled: boolean;
  sendUsingBot: boolean;
  sendUsingBotOriginal: boolean;
  creatingNewMessage: boolean;
  applicationSettings: ITargetingApplications;
  push: boolean;
  pushOriginal?: boolean;
  targetingExistingStructure: ITargetingExistingStrucuture;
  iaTargetingTargetContent: boolean;
  iaTargetingOperator: string;
  iaTargetingOperatorADGroups: string;
  targetingFields: ITargetingFields[];
  iaTargetingGroupData: string;
  isDraft: boolean;
  approvalStatus: "Pending" | "Approved" | "Rejected";
  tenantSettings: ITenantSettings;
  likesEnabledOnInstance: boolean;
  commentsEnabledOnInstance: boolean;
  likesEnabled: boolean;
  commentsEnabled: boolean;
  approvers?: IApprover[];
  close: (message: IMessage) => boolean;
  closeAndReload: () => void;
  delete: () => void;
  isReloadOk: (message: IMessage) => boolean;
  title?: IComposerComponent;
  teaser?: IComposerComponent;
  location?: IComposerComponent;
  cover?: IComposerComponent;
  subjects?: IComposerComponent;
  contacts?: IComposerComponent;
  attachments?: IComposerComponent;
  contentComponents?: IComposerComponent[];
  htmlContent?: string;
  configuration?: IMessageIAConfiguration;
  marginTop: number;
  dataAutomationIdPrefix: string;
  isFullPageView: boolean;
  selectedFeeds: string[];
  isAdminPage?: boolean;
  isPinned?: boolean;
  pinnedEndDate?: Date;
  publishToReplayByDefault?: boolean;
  getUserRole?: () => void;
  isNewsLink?: boolean;
  newsLink?: string;
  openNewsLinkInNewTab?: boolean;
  isMessagesTemplateEditor?: boolean;
  fontSlots?: IFontSlots;
  saveTemplateCallback?: (jsonTemplate: string) => void;
  messageSaved: (message: IMessage) => void;
  isCreatingFromTemplate?: boolean;
  recurrenceInfo?: {
    isRecurring?: boolean;
    recurrenceData?: IRecurrenceData;
    isEditingOccurrence?: boolean;
    occurrenceSeriesId?: number;
    occurrenceStartDate?: Date;
  };
}

export class IAComposer extends React.Component<IComposerProps> {

  private store: Store;

  constructor(props: IComposerProps) {
    super(props);
    this.store = new Store();
    configure({
      isolateGlobalState: true,
      enforceActions: "never"
    });
    this.updateStore(props);
  }

  public componentWillReceiveProps(props: IComposerProps) {
    if (props.id && props.id !== this.props.id
      || props.creatingNewMessage != undefined && this.props.creatingNewMessage == undefined
      || this.props.open && !props.open
      || props.instance !== this.props.instance
      || props.userRole !== this.props.userRole) {
      this.updateStore(props);
    }
  }

  private updateStore(props: IComposerProps): void {
    this.store.id = props.id;
    this.store.open = props.open;
    this.store.token = props.token;
    this.store.tenant = props.tenant;
    this.store.environment = props.environment;
    this.store.instance = props.instance;
    this.store.component = props.component;
    this.store.user = props.user;
    this.store.userRole = props.userRole;
    this.store.author = props.author;
    this.store.highlightColor = props.highlightColor;
    this.store.placeholderColor = props.placeholderColor;
    this.store.imageList = props.imageList;
    this.store.videoList = props.videoList;
    this.store.isMobile = props.isMobile;
    this.store.startDate = props.startDate;
    this.store.endDate = props.endDate;
    this.store.allDayEvent = props.allDayEvent;
    this.store.isBotInstalled = props.isBotInstalled;
    this.store.sendUsingBot = props.sendUsingBot;
    this.store.sendUsingBotOriginal = props.sendUsingBotOriginal;
    this.store.creatingNewMessage = props.creatingNewMessage;
    this.store.applicationSettings = props.applicationSettings;
    this.store.push = props.push;
    this.store.pushOriginal = props.pushOriginal;
    this.store.targetingExistingStructure = props.targetingExistingStructure;
    this.store.iaTargetingTargetContent = props.iaTargetingTargetContent;
    this.store.iaTargetingOperator = props.iaTargetingOperator ? props.iaTargetingOperator : "OR";
    this.store.iaTargetingOperatorADGroups = props.iaTargetingOperatorADGroups;
    this.store.targetingFields = props.targetingFields;
    this.store.iaTargetingGroupData = props.iaTargetingGroupData;
    this.store.isDraft = props.isDraft;
    this.store.isDraftOriginally = props.isDraft;
    this.store.approvalStatus = props.approvalStatus;
    this.store.originalApprovalStatus = props.approvalStatus;
    this.store.tenantSettings = props.tenantSettings;
    this.store.likesEnabledOnInstance = props.likesEnabledOnInstance;
    this.store.commentsEnabledOnInstance = props.commentsEnabledOnInstance;
    this.store.likesEnabled = props.likesEnabled;
    this.store.commentsEnabled = props.commentsEnabled;
    this.store.approvers = props.approvers;
    this.store.title = props.title;
    this.store.teaser = props.teaser;
    this.store.location = props.location;
    this.store.cover = props.cover;
    this.store.subjects = props.subjects;
    this.store.contacts = props.contacts;
    this.store.attachments = props.attachments;
    this.store.contentComponents = props.contentComponents;
    this.store.htmlContent = props.htmlContent;
    this.store.marginTop = props.marginTop;
    this.store.dataAutomationIdPrefix = props.dataAutomationIdPrefix;
    this.store.isFullPageView = props.isFullPageView;
    this.store.isAdminPage = props.isAdminPage;
    this.store.selectedFeeds = props.selectedFeeds;
    this.store.isPinned = props.isPinned;
    this.store.pinnedEndDate = props.pinnedEndDate;
    this.store.isNewsLink = props.isNewsLink;
    this.store.newsLink = props.newsLink;
    this.store.openNewsLinkInNewTab = props.openNewsLinkInNewTab;
    this.store.isMessagesTemplateEditor = props.isMessagesTemplateEditor;
    this.store.fontSlots = props.fontSlots;
    this.store.isCreatingFromTemplate = props.isCreatingFromTemplate;
    this.store.recurrenceInfo = props.recurrenceInfo;

    if (props.selectedFeeds?.length > 0 || props.publishToReplayByDefault) {
      this.store.publishToReplay = true;
    }
    this.store.configuration = props.configuration;
    if (!this.store.configuration) {
      this.store.configuration = {
        replay: {
          id: "Replay",
          type: "cover",
          typeToEdit: "image",
          boxStyle: undefined,
          content: undefined,
          image: {
            source: "Link",
          },
          video: {
            source: "Link",
          }
        }
      }
    } else {
      if (!this.store.configuration.replay) {
        this.store.configuration = {
          replay: {
            id: "Replay",
            type: "cover",
            typeToEdit: "image",
            boxStyle: undefined,
            content: undefined,
            image: {
              source: "Link",
            },
            video: {
              source: "Link",
            }
          }
        }
      }
    }
    if (!this.props.userRole && props.instance && this.props.getUserRole) {
      this.props.getUserRole();
    }
  }

  public render(): JSX.Element {
    return (
      <Provider store={this.store}>
        <Composer
          open={this.props.open}
          close={(message: IMessage) => this.props.close(message)}
          closeAndReload={() => this.props.closeAndReload()}
          delete={() => this.props.delete()}
          isReloadOk={(message: IMessage) => this.props.isReloadOk(message)}
          saveTemplateCallback={this.props.saveTemplateCallback}
          messageSaved={(message: IMessage) => this.props.messageSaved(message)}
        />
      </Provider>
    );
  }


}